import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import Logo from '../../images/pngs/Logo.png';
import { LoginContext } from '../../Contexts/LoginContext';
import ProfileDrawer from '../../Components/buyer/ProfileDrawer';
import NotificationPopup from '../buyer/Notifications';
import LocationCurrencyModal, { getFlagUrl } from '../utils/LocationCurrencyModal';
import AvailabilityModal from './AvailabilityModal';
import { FaBars, FaCommentDots, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import { formatCurrency } from '../../data/constants';
import { PiUserSwitchFill } from 'react-icons/pi';
import { FaShop } from 'react-icons/fa6';
import axios from 'axios';
import io from 'socket.io-client';

export default function SellerHeader() {
  const { isLogin, isTablet, user, logout, currency, exchangeRate } = useContext(LoginContext);
  const headerLinks = isLogin
    ? ['dashboard', 'postings', 'your orders', 'earnings', 'trade lead', 'upgrade']
    : ['dashboard', 'postings'];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [sellerOrderUnread, setSellerOrderUnread] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(null);
  const socketRef = useRef(null);
  const location = useLocation();

  const fetchUnread = useCallback(async () => {
    if (!isLogin || !user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/chats/userChats/${user._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessages(res.data.totalUnread || 0);
    } catch (err) { /* silent */ }
  }, [isLogin, user?._id]);

  const fetchOrderUnread = useCallback(async () => {
    if (!isLogin || !user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/notifications/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSellerOrderUnread(res.data.sellerUnread || 0);
    } catch (err) { /* silent */ }
  }, [isLogin, user?._id]);

  const fetchAvailableBalance = useCallback(async () => {
    if (!isLogin || !user?.sellerId?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/${user.sellerId._id}/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableBalance(res.data?.earnings?.availableBalance ?? null);
    } catch (err) { setAvailableBalance(null); }
  }, [isLogin, user?.sellerId?._id]);

  useEffect(() => {
    fetchUnread();
    fetchOrderUnread();
    fetchAvailableBalance();
    const interval = setInterval(() => { fetchUnread(); fetchOrderUnread(); fetchAvailableBalance(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread, fetchOrderUnread, fetchAvailableBalance]);

  useEffect(() => { fetchUnread(); fetchOrderUnread(); fetchAvailableBalance(); }, [location.pathname, fetchUnread, fetchOrderUnread, fetchAvailableBalance]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') { fetchUnread(); fetchOrderUnread(); fetchAvailableBalance(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchUnread, fetchOrderUnread, fetchAvailableBalance]);

  useEffect(() => {
    if (!isLogin || !user?._id) return;
    const socket = io(process.env.REACT_APP_Backend_Host_Name, { auth: { token: localStorage.getItem('token') } });
    socketRef.current = socket;
    socket.on('orderUnreadRefresh', () => fetchOrderUnread());
    socket.on('notificationRefresh', () => fetchUnread());
    socket.on('receiveMessage', () => fetchUnread());
    return () => socket.disconnect();
  }, [isLogin, user?._id, fetchOrderUnread, fetchUnread]);

  const userCountryCode = user?.buyerAddress?.country?.code || '';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (isMobileMenuOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'unset';
  }

  // Component for Navigation Links
  const NavigationLinks = ({ className, onClick }) => (
    <ul className={className}>
      {headerLinks.map((link) => (
        <li key={link}>
          {link === 'your orders' ? (
            <NavLink
              to="/seller/orders"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={onClick}
            >
              Orders
              {sellerOrderUnread > 0 && <span className="headerOrderUnreadBadge">{sellerOrderUnread}</span>}
            </NavLink>
          ) : (
            <NavLink
              to={link === 'dashboard' ? 'sellerDashboard' : `/${link.replace(/\s+/g, '')}`}
              className={({ isActive }) => (isActive ? 'active' : '')}
              end={link === 'dashboard'}
              onClick={onClick}
            >
              {link.charAt(0).toUpperCase() + link.slice(1)}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );

  // Component for Auth Buttons
  const AuthButtons = ({ className }) => (
    <div className={className}>
      <Link to="/signin" className="signin">Sign In</Link>
      <Link to="/signup" className="signup">Sign Up</Link>
    </div>
  );

  const MobileIconsSection = ({ className, onClick }) => (
    <div className={className}>
      {user?.role === 'seller' && user?.sellerId && availableBalance != null && (
        <Link to="/earnings" className="mobileBalanceLink" onClick={onClick}>
          Balance: {formatCurrency(availableBalance, currency || 'USD', exchangeRate ?? 1)}
        </Link>
      )}
      {user?.role === 'seller' && user?.sellerId && (
        <button type="button" onClick={() => { setIsAvailabilityModalOpen(true); setIsMobileMenuOpen(false); }}>
          <FaCalendarAlt className="icon" /> Set Availability
        </button>
      )}
      <Link
        to={"/seller/settings"}
        state={{ isSellerAccount: true }}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        Settings
      </Link>
      <Link to={`${"/seller/contact"}`} onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
      <Link to={`${"/seller/myReviews"}`} onClick={() => setIsMobileMenuOpen(false)}>Ratings & feedback</Link>
      <Link onClick={() => { logout(); setIsMobileMenuOpen(false) }}>Logout</Link>

      {(user?.role === "seller" && user?.sellerId) ? <Link to={'/'} className="switchToSelling">
        <PiUserSwitchFill className='icon' />
        {'Switch to Buying'}
      </Link> : <Link to={'/becomeSeller'} className="switchToSelling">
        <FaShop className='icon' />
        Become a Seller
      </Link>}
    </div>
  );

  return (
    <header className="buyerHeader" style={isTablet && isMobileMenuOpen ? { position: "fixed", width: "100%", top: "0", zIndex: "100" } : {}}>
      <div className="buyerHeaderLogo">
        <img src={Logo} alt="Seller Logo" />
      </div>

      {/* Desktop Navigation */}
      <NavigationLinks className="buyerHeaderLinks" />

      {/* Mobile Menu Button */}
      <button
        className="mobileMenuButton"
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      {isLogin ? (
        <div className="buyerHeaderIcons">
          {user?.role === 'seller' && user?.sellerId && (
            <button
              className="headerAvailabilityBtn"
              onClick={() => setIsAvailabilityModalOpen(true)}
              title="Set availability"
              aria-label="Set availability"
            >
              <FaCalendarAlt className='calendarIcon' />
              <span className="headerAvailabilityLabel">Availability</span>
            </button>
          )}
          <button className="headerCountryBtn" onClick={() => setIsLocationModalOpen(true)}>
            {getFlagUrl(userCountryCode) ? (
              <img src={getFlagUrl(userCountryCode)} alt={userCountryCode} className="flagImg" />
            ) : (
              <span className="flagEmoji">🌍</span>
            )}
            {userCountryCode && <span className="countryCode">{userCountryCode}</span>}
          </button>
          <NavLink to="/seller/chats" className="cartIconButton">
            <FaCommentDots size={22} />
            {unreadMessages > 0 && (
              <span className="cartItemCount">{unreadMessages}</span>
            )}
          </NavLink>
          {!isTablet && <>
            <NotificationPopup />
            {user?.role === 'seller' && user?.sellerId && availableBalance != null && (
              <Link to="/earnings" className="headerBalanceBadge" title="Available balance">
                {formatCurrency(availableBalance, currency || 'USD', exchangeRate ?? 1)}
              </Link>
            )}
            <ProfileDrawer isSeller={true} />
          </>}
        </div>
      ) : (
        <AuthButtons className="buyerHeaderAuth" />
      )}

      <div className={`mobileNav ${isMobileMenuOpen ? 'active' : ''}`}>
        <NavigationLinks
          className="mobileNavLinks"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        {isLogin ? (
          <MobileIconsSection
            className="mobileNavIcons"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        ) : (
          <AuthButtons className="mobileNavAuth" />
        )}
      </div>

      <LocationCurrencyModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />
      <AvailabilityModal
        isOpen={isAvailabilityModalOpen}
        onClose={() => setIsAvailabilityModalOpen(false)}
      />
    </header>
  );
}